<script setup>
// ============================================================================
// BonBoZ: BỘ CHỌN DÙNG CHUNG cho "mẫu trả lời theo trang" (CannedByPage.vue)
// và "tag theo trang" (pageLabels/LabelPicker.vue). Hai màn trước đây là bản
// sao của nhau — lưới hai cột chỉ hiện mã tắt, không tìm, không nhóm — nên sửa
// một chỗ cho cả hai. Xem issue #148.
//
// Chỉ lo phần CHỌN: tìm, nhóm, tick cả nhóm, tick/bỏ những gì đang hiện, chép
// từ trang khác, đánh dấu mục vừa đổi so với bản đã lưu. Lưu và câu thông báo
// do cha làm — nghĩa của "bỏ tick hết" khác nhau ở hai màn (mẫu: chỉ còn mẫu
// chung; tag: hiện tất cả tag) nên cha mới nói đúng được.
//
// Giá trị là một Set qua v-model; luôn emit Set MỚI để Vue nhận ra thay đổi.
// Tính toán thuần nằm ở `helper/bonboz/boChon.js`.
// ============================================================================
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { locMuc, nhomMuc, chepTu } from 'dashboard/helper/bonboz/boChon';
import Button from 'dashboard/components-next/button/Button.vue';
import ChonMuc from 'dashboard/components/ui/SelectMenu.vue';

const props = defineProps({
  // [{ key, nhan, phu?, mau?, nhom? }]
  muc: { type: Array, required: true },
  // [{ id, ten, mau }] — null = không nhóm (tag chưa có khái niệm chủ đề)
  nhom: { type: Array, default: null },
  modelValue: { type: Set, required: true },
  // Bản đã lưu, để đánh dấu mục vừa thêm / vừa bỏ.
  goc: { type: Set, required: true },
  // Trang đang sửa — bị loại khỏi danh sách "chép từ trang".
  trangHienTai: { type: [String, Number], default: '' },
  // inboxId -> Set khoá của trang đó
  layCuaTrang: { type: Function, required: true },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const tuKhoa = ref('');
const chiHienTick = ref(false);
const trangNguon = ref('');
// Nhóm đang MỞ. Mặc định gập hết: 54 chủ đề gập lại vừa một màn hình, mở hết
// thì lại thành danh sách 265 dòng như cũ.
const moNhom = ref(new Set());

const chon = computed(() => props.modelValue);
const datLai = next => emit('update:modelValue', next);

const dangHien = computed(() => {
  const loc = locMuc(props.muc, tuKhoa.value);
  return chiHienTick.value ? loc.filter(m => chon.value.has(m.key)) : loc;
});

// Đang tìm hoặc đang lọc "chỉ mục đã tick" thì mở hết — giấu kết quả sau một
// tiêu đề gập là bắt người dùng bấm thêm để thấy cái mình vừa tìm.
const moHet = computed(() => !!tuKhoa.value.trim() || chiHienTick.value);

// Không nhóm (tag) = một nhóm duy nhất không có tiêu đề, luôn mở — để template
// chỉ có một kiểu dòng.
const cacNhom = computed(() =>
  props.nhom
    ? nhomMuc(dangHien.value, props.nhom, t('BONBOZ.BO_CHON.KHONG_NHOM'))
    : [{ id: 'tat-ca', khongTieuDe: true, muc: dangHien.value }]
);

// Chỉ liệt kê trang CÓ gì để chép. Trang chưa cấu hình mà nằm trong danh sách
// thì một cú bấm nhầm là xoá sạch mọi ô đang tick — không xác nhận, không hoàn tác.
const trangKhac = computed(() =>
  inboxes.value.filter(
    i =>
      String(i.id) !== String(props.trangHienTai) &&
      chepTu(props.layCuaTrang(i.id), props.muc).size > 0
  )
);

const doiMot = key => {
  const next = new Set(chon.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  datLai(next);
};

// Tick / bỏ một loạt: áp lên những mục ĐANG HIỆN, không phải cả kho — vừa tìm
// "bao hanh" xong bấm "tick tất cả" là muốn đúng mấy mẫu bảo hành đang thấy.
const datNhieu = (ds, bat) => {
  const next = new Set(chon.value);
  ds.forEach(m => (bat ? next.add(m.key) : next.delete(m.key)));
  datLai(next);
};

const soTick = ds => ds.filter(m => chon.value.has(m.key)).length;

const doiNhom = n => {
  datNhieu(n.muc, soTick(n.muc) !== n.muc.length);
};

const doiMo = id => {
  const next = new Set(moNhom.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  moNhom.value = next;
};
const dangMo = n => n.khongTieuDe || moHet.value || moNhom.value.has(n.id);

const chep = inboxId => {
  if (!inboxId) return;
  const moi = chepTu(props.layCuaTrang(inboxId), props.muc);
  const ten = inboxes.value.find(i => String(i.id) === String(inboxId))?.name;
  // Lưới an toàn cho lọc ở `trangKhac`: chép ra 0 mục thì giữ nguyên ô tick.
  if (!moi.size) {
    useAlert(t('BONBOZ.BO_CHON.KHONG_CO_GI_DE_CHEP', { trang: ten || '' }));
  } else {
    datLai(moi);
    useAlert(t('BONBOZ.BO_CHON.DA_CHEP', { n: moi.size, trang: ten || '' }));
  }
  // Trả ô về trống để chép tiếp từ trang khác (hoặc chính trang đó) được.
  trangNguon.value = '';
};

const trangThai = key => {
  const co = chon.value.has(key);
  const daLuu = props.goc.has(key);
  if (co && !daLuu) return 'them';
  if (!co && daLuu) return 'bo';
  return '';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-2">
      <div class="relative min-w-48 flex-[2]">
        <i
          class="i-lucide-search pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-10 ltr:left-2 rtl:right-2"
        />
        <input
          v-model="tuKhoa"
          type="search"
          class="!mb-0 !h-8 !text-sm ltr:!pl-8 rtl:!pr-8"
          :placeholder="$t('BONBOZ.BO_CHON.TIM')"
          data-testid="bo-chon-tim"
        />
      </div>
      <div v-if="trangKhac.length" class="min-w-40 flex-1">
        <ChonMuc
          :model-value="trangNguon"
          :muc="trangKhac"
          hien-avatar
          icon="i-lucide-copy"
          :all-label="$t('BONBOZ.BO_CHON.CHEP_TU')"
          @update:model-value="chep"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2 text-sm">
      <Button
        size="xs"
        slate
        faded
        :label="$t('BONBOZ.BO_CHON.TICK_HET')"
        :disabled="!dangHien.length"
        @click="datNhieu(dangHien, true)"
      />
      <Button
        size="xs"
        slate
        faded
        :label="$t('BONBOZ.BO_CHON.BO_HET')"
        :disabled="!soTick(dangHien)"
        @click="datNhieu(dangHien, false)"
      />
      <label
        class="!mb-0 flex cursor-pointer items-center gap-1.5 text-n-slate-11"
      >
        <input v-model="chiHienTick" type="checkbox" class="!mb-0" />
        {{ $t('BONBOZ.BO_CHON.CHI_HIEN_TICK') }}
      </label>
      <span class="text-n-slate-11 ltr:ml-auto rtl:mr-auto">
        {{ $t('BONBOZ.BO_CHON.DEM', { chon: chon.size, tong: muc.length }) }}
      </span>
    </div>

    <div
      class="max-h-[50vh] overflow-y-auto rounded-lg border border-n-weak"
      data-testid="bo-chon-danh-sach"
    >
      <p v-if="!dangHien.length" class="m-0 p-3 text-sm text-n-slate-11">
        {{ $t('BONBOZ.BO_CHON.KHONG_KHOP') }}
      </p>

      <template v-else>
        <div
          v-for="n in cacNhom"
          :key="n.id ?? 'le'"
          class="border-b border-n-weak last:border-b-0"
        >
          <div
            v-if="!n.khongTieuDe"
            class="sticky top-0 z-10 flex items-center gap-2 bg-n-solid-2 px-2 py-1.5"
          >
            <input
              type="checkbox"
              class="!mb-0 shrink-0"
              :checked="soTick(n.muc) === n.muc.length"
              :indeterminate="soTick(n.muc) > 0 && soTick(n.muc) < n.muc.length"
              :aria-label="n.ten"
              @change="doiNhom(n)"
            />
            <button
              type="button"
              class="flex min-w-0 flex-1 items-center gap-1.5 p-0 text-left text-sm font-medium text-n-slate-12"
              @click="doiMo(n.id)"
            >
              <i
                class="size-4 shrink-0 text-n-slate-10"
                :class="
                  dangMo(n) ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
                "
              />
              <span
                v-if="n.mau"
                class="inline-block size-2.5 shrink-0 rounded-sm"
                :style="{ backgroundColor: n.mau }"
              />
              <span class="truncate">{{ n.ten }}</span>
              <span class="shrink-0 text-xs font-normal text-n-slate-11">
                {{
                  $t('BONBOZ.BO_CHON.DEM_NHOM', {
                    chon: soTick(n.muc),
                    tong: n.muc.length,
                  })
                }}
              </span>
            </button>
          </div>
          <div
            v-if="dangMo(n)"
            class="p-1"
            :class="{ 'ltr:pl-6 rtl:pr-6': !n.khongTieuDe }"
          >
            <label
              v-for="m in n.muc"
              :key="m.key"
              class="!mb-0 flex min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-n-alpha-1"
              :class="{
                'bg-n-teal-2': trangThai(m.key) === 'them',
                'bg-n-ruby-2': trangThai(m.key) === 'bo',
              }"
              :title="m.phu || m.nhan"
            >
              <input
                type="checkbox"
                class="!mb-0 shrink-0"
                :checked="chon.has(m.key)"
                @change="doiMot(m.key)"
              />
              <span
                v-if="m.mau"
                class="inline-block size-2.5 shrink-0 rounded-sm"
                :style="{ backgroundColor: m.mau }"
              />
              <span
                class="max-w-[40%] shrink-0 truncate font-medium text-n-slate-12"
              >
                {{ m.nhan }}
              </span>
              <span class="min-w-0 flex-1 truncate text-n-slate-11">{{
                m.phu
              }}</span>
              <span
                v-if="trangThai(m.key)"
                class="shrink-0 text-xs font-medium"
                :class="
                  trangThai(m.key) === 'them'
                    ? 'text-n-teal-11'
                    : 'text-n-ruby-11'
                "
              >
                {{
                  trangThai(m.key) === 'them'
                    ? $t('BONBOZ.BO_CHON.MOI_THEM')
                    : $t('BONBOZ.BO_CHON.MOI_BO')
                }}
              </span>
            </label>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
